import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../../core/tools/interfaces/ITool';
import { ICommand } from '../../../core/commands/ICommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { getTranslatedPersonProperties } from '../shapes/person.manifest';
import { UpdatePersonCommand } from '../commands/UpdatePersonCommand';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group';
import { Switch } from '../../../components/ui/switch';
import { Plus, Minus } from 'lucide-react';
import { Textarea } from '../../../components/ui/textarea';
import { MultiSelectRenderer } from '../fields/components/MultiSelectRenderer';
import { FieldType, type FieldManifest } from '../../../core/fields/types';
import { useFieldVisibilityStore } from '../../../stores/fieldVisibilityStore';
import {
  normalizePersonUpdates,
  resolvePersonFieldFromAttrs,
} from '../utils/personUpdateNormalizer';
import { useTranslation } from '../../../hooks/useTranslation';
import { bindGenogramToolName } from './utils/toolNameI18n';
import i18n from '@/i18n/config';
import { Tracker } from '../../../domains/analytics/services/Tracker';
import { MEDICAL } from '../../../domains/analytics/constants/MetricEvents';

// Wrapper component that handles canvas clicks for Input/Textarea
const CommitOnCanvasClickInput: React.FC<{
  value: string;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  onCommit: () => void;
  className?: string;
  dataTestId?: string;
}> = ({ value, placeholder, onChange, onBlur, onCommit, className, dataTestId }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const isFocusedRef = React.useRef(false);

  React.useEffect(() => {
    const getInput = () => {
      return containerRef.current?.querySelector('input') as HTMLInputElement | null;
    };

    const input = getInput();
    if (!input) return;

    const commitValue = () => {
      if (isFocusedRef.current) {
        onCommit();
        isFocusedRef.current = false;
      }
    };

    // Attach to canvas directly
    const canvasContainer = document.querySelector('[data-testid="canvas-stage"]');
    const handleCanvasClick = () => {
      const currentInput = getInput();
      if (isFocusedRef.current && currentInput) {
        commitValue();
        if (document.activeElement === currentInput) {
          currentInput.blur();
        }
      }
    };

    if (canvasContainer) {
      canvasContainer.addEventListener('mousedown', handleCanvasClick, true);
      canvasContainer.addEventListener('pointerdown', handleCanvasClick, true);
    }

    const handleFocusOut = () => {
      setTimeout(() => {
        const currentInput = getInput();
        if (currentInput && document.activeElement !== currentInput) {
          commitValue();
        }
      }, 0);
    };

    input.addEventListener('focusout', handleFocusOut);
    input.addEventListener('focus', () => { isFocusedRef.current = true; });

    return () => {
      input.removeEventListener('focusout', handleFocusOut);
      if (canvasContainer) {
        canvasContainer.removeEventListener('mousedown', handleCanvasClick, true);
        canvasContainer.removeEventListener('pointerdown', handleCanvasClick, true);
      }
    };
  }, [onCommit]);

  return (
    <div ref={containerRef}>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onBlur={onBlur}
        className={className}
        data-testid={dataTestId}
      />
    </div>
  );
};

const CommitOnCanvasClickTextarea: React.FC<{
  value: string;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur: () => void;
  onCommit: () => void;
  className?: string;
  rows?: number;
  dataTestId?: string;
}> = ({ value, placeholder, onChange, onBlur, onCommit, className, rows, dataTestId }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const isFocusedRef = React.useRef(false);

  React.useEffect(() => {
    const getTextarea = () => {
      return containerRef.current?.querySelector('textarea') as HTMLTextAreaElement | null;
    };

    const textarea = getTextarea();
    if (!textarea) return;

    const commitValue = () => {
      if (isFocusedRef.current) {
        onCommit();
        isFocusedRef.current = false;
      }
    };

    // Attach to canvas directly
    const canvasContainer = document.querySelector('[data-testid="canvas-stage"]');
    const handleCanvasClick = () => {
      const currentTextarea = getTextarea();
      if (isFocusedRef.current && currentTextarea) {
        commitValue();
        if (document.activeElement === currentTextarea) {
          currentTextarea.blur();
        }
      }
    };

    if (canvasContainer) {
      canvasContainer.addEventListener('mousedown', handleCanvasClick, true);
      canvasContainer.addEventListener('pointerdown', handleCanvasClick, true);
    }

    const handleFocusOut = () => {
      setTimeout(() => {
        const currentTextarea = getTextarea();
        if (currentTextarea && document.activeElement !== currentTextarea) {
          commitValue();
        }
      }, 0);
    };

    textarea.addEventListener('focusout', handleFocusOut);
    textarea.addEventListener('focus', () => { isFocusedRef.current = true; });

    return () => {
      textarea.removeEventListener('focusout', handleFocusOut);
      if (canvasContainer) {
        canvasContainer.removeEventListener('mousedown', handleCanvasClick, true);
        canvasContainer.removeEventListener('pointerdown', handleCanvasClick, true);
      }
    };
  }, [onCommit]);

  return (
    <div ref={containerRef}>
      <Textarea
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onBlur={onBlur}
        className={className}
        rows={rows}
        data-testid={dataTestId}
      />
    </div>
  );
};

type Person = any;

function computeMixed<T>(values: Array<T | undefined>): T | 'mixed' | undefined {
  const present = values.filter(v => v !== undefined);
  if (present.length === 0) return undefined;
  const first = present[0];
  const equals = (a: unknown, b: unknown): boolean => {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    }
    return a === b;
  };
  for (const v of present) {
    if (!equals(v, first)) return 'mixed';
  }
  return first;
}

const formatDateForInput = (value: unknown): string => {
  if (typeof value !== 'string' || value.length === 0) return '';
  if (value === 'mixed') return '';
  const trimmed = value.trim();
  if (trimmed.length === 0) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const isoCandidate = trimmed.split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoCandidate)) return isoCandidate;
  const parsed = Date.parse(trimmed);
  if (!Number.isNaN(parsed)) {
    return new Date(parsed).toISOString().slice(0, 10);
  }
  return trimmed;
};

const SECTION_CONFIG = {
  identity: {
    titleKey: 'schema.sections.coreIdentity' as const,
    sectionIds: ['identity']
  },
  medical: {
    titleKey: 'schema.sections.medicalHealth' as const,
    sectionIds: ['medical', 'mental-health', 'genetic']
  },
  legal: {
    titleKey: 'schema.sections.legalEstate' as const,
    sectionIds: ['legal']
  },
  financial: {
    titleKey: 'schema.sections.financial' as const,
    sectionIds: ['financial']
  },
  demographics: {
    titleKey: 'schema.sections.socialProfessional' as const,
    sectionIds: ['social', 'professional']
  },
  relationships: {
    titleKey: 'schema.sections.relationships' as const,
    sectionIds: ['relationships']
  },
  research: {
    titleKey: 'schema.sections.research' as const,
    sectionIds: ['research']
  },
  context: {
    titleKey: 'schema.sections.contactContext' as const,
    sectionIds: ['contact']
  },
  extras: {
    titleKey: 'schema.sections.notesAdditional' as const,
    sectionIds: ['notes']
  }
} as const;

const pendingFieldCache = new Map<
  string,
  { drafts: Record<string, string>; lastSubmitted: Record<string, string> }
>();

const SchemaDrivenPersonPanel: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('genogram');
  const ids = Array.from(context.selection);
  const nodes = useDiagramStore(s => s.nodes);
  const persons: Person[] = nodes.filter(n => ids.includes(n.id));
  const selectedUseCase = useWorkflowStore(s => s.selectedUseCase);
  const enabledFieldIds = useFieldVisibilityStore(s => s.enabledFieldIds);
  const [sectionsOpen, setSectionsOpen] = React.useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('personPanel.sectionsOpen');
      return saved ? JSON.parse(saved) : {
        identity: true,
        medical: false,
        legal: false,
        financial: false,
        demographics: false,
        relationships: false,
        research: false,
        context: false,
        extras: false
      };
    } catch {
      return {
        identity: true,
        medical: false,
        legal: false,
        financial: false,
        demographics: false,
        relationships: false,
        research: false,
        context: false,
        extras: false
      };
    }
  });

  const [showAllFields, setShowAllFields] = React.useState<Record<string, boolean>>({});

  const toggleSection = (key: string, hasHiddenFieldsOnly: boolean) => {
    setSectionsOpen(prev => {
      const wasOpen = prev[key];
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem('personPanel.sectionsOpen', JSON.stringify(next));
      } catch {}

      // If opening a section that has only hidden fields, automatically show all fields
      if (next[key] && hasHiddenFieldsOnly) {
        setShowAllFields(prevShow => ({ ...prevShow, [key]: true }));
      }

      // Track medical section opening (only when opening, not closing)
      const medicalSections = ['medical', 'genetic', 'mental-health'];
      if (!wasOpen && next[key] && medicalSections.includes(key)) {
        Tracker.track(MEDICAL.SECTION_OPENED, {
          section_id: key,
          use_case: selectedUseCase || undefined,
        });
      }

      return next;
    });
  };

  const toggleShowAllFields = (key: string) => {
    setShowAllFields(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const allDefs = getTranslatedPersonProperties(t as any) as Array<any>;

  const groups = ['identity', 'medical', 'legal', 'financial', 'demographics', 'relationships', 'research', 'context', 'extras'] as const;
  const shouldRenderDef = React.useCallback((def: any) => def?.key !== 'notes', []);

  // Group ALL properties (without use-case filtering)
  const allDefsByGroup: Record<string, Array<any>> = {
    identity: [], medical: [], legal: [], financial: [], demographics: [], relationships: [], research: [], context: [], extras: []
  };
  for (const def of allDefs) {
    if (!shouldRenderDef(def)) continue;
    const g = def.ui?.group || 'extras';
    if (!enabledFieldIds || enabledFieldIds.size === 0 || enabledFieldIds.has(def.key)) {
      allDefsByGroup[g].push(def);
    }
  }

  // Filter properties based on active use case
  const filteredDefsByGroup: Record<string, Array<any>> = {
    identity: [], medical: [], legal: [], financial: [], demographics: [], relationships: [], research: [], context: [], extras: []
  };
  for (const def of allDefs) {
    if (!shouldRenderDef(def)) continue;
    const useCases = def.ui?.useCases;
    const isVisible = !useCases || !Array.isArray(useCases) || useCases.length === 0 || (selectedUseCase && useCases.includes(selectedUseCase));
    if (isVisible && (!enabledFieldIds || enabledFieldIds.size === 0 || enabledFieldIds.has(def.key))) {
      const g = def.ui?.group || 'extras';
      filteredDefsByGroup[g].push(def);
    }
  }

  // Sort both by priority
  groups.forEach(g => {
    // Higher priority first (descending) so key fields like 'name' appear at the top
    allDefsByGroup[g].sort((a, b) => (b.ui?.priority ?? 0) - (a.ui?.priority ?? 0));
    filteredDefsByGroup[g].sort((a, b) => (b.ui?.priority ?? 0) - (a.ui?.priority ?? 0));
  });

  const TAG_FIELDS = React.useMemo(
    () => new Set(['culturalHeritageTags', 'medicalConditionTags', 'tags']),
    [],
  );

  const normalizeUpdates = React.useCallback((updates: Record<string, any>) => {
    return normalizePersonUpdates(updates);
  }, []);

  const coerceBoolean = React.useCallback((value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
      if (['false', '0', 'no', 'n'].includes(normalized)) return false;
    }
    if (typeof value === 'object' && value !== null && typeof (value as any).valueOf === 'function') {
      const primitive = (value as any).valueOf();
      return coerceBoolean(primitive);
    }
    return undefined;
  }, []);

  const resolveFieldValue = React.useCallback((person: Person, key: string) => {
    const attrs = ((person as any)?.attrs ?? {}) as Record<string, unknown>;
    const props = ((person as any)?.props ?? {}) as Record<string, unknown>;

    const mirrored = resolvePersonFieldFromAttrs(person as Record<string, unknown>, key, attrs);
    if (mirrored !== undefined) {
      return mirrored;
    }

    if (key === 'transgender') {
      const direct = coerceBoolean((person as any).transgender);
      if (typeof direct === 'boolean') return direct;
      const attrValue = coerceBoolean(attrs.transgender);
      if (typeof attrValue === 'boolean') return attrValue;
      return attrs.genderIdentity === 'transgender' ? true : undefined;
    }

    if (key === 'deceased') {
      const direct = coerceBoolean((person as any).deceased);
      if (typeof direct === 'boolean') return direct;
      const attrValue = coerceBoolean(attrs.deceased);
      if (typeof attrValue === 'boolean') return attrValue;
      const attrIsDeceased = coerceBoolean(attrs.isDeceased);
      if (typeof attrIsDeceased === 'boolean') return attrIsDeceased;
      return undefined;
    }

    if (key === 'pregnancy') {
      const direct = coerceBoolean((person as any).pregnancy);
      if (typeof direct === 'boolean') return direct;
      const attrValue = coerceBoolean(attrs.pregnancy);
      if (typeof attrValue === 'boolean') return attrValue;
      return undefined;
    }

    if (key === 'stillbirth') {
      const direct = coerceBoolean((person as any).stillbirth);
      if (typeof direct === 'boolean') return direct;
      const attrValue = coerceBoolean(attrs.stillbirth);
      if (typeof attrValue === 'boolean') return attrValue;
      return undefined;
    }   
    if (key === 'immigration') {
      const direct = coerceBoolean((person as any).immigration);
      if (typeof direct === 'boolean') return direct;
      const attrValue = coerceBoolean(attrs.immigration);
      if (typeof attrValue === 'boolean') return attrValue;
      return undefined;
    }
    if (key === 'culture') {
      const direct = coerceBoolean((person as any).culture);
      if (typeof direct === 'boolean') return direct;
      const attrValue = coerceBoolean(attrs.culture);
      if (typeof attrValue === 'boolean') return attrValue;
      return undefined;
    }

    if (TAG_FIELDS.has(key)) {
      const topLevel = (person as any)[key];
      if (Array.isArray(topLevel)) {
        return topLevel;
      }
      const attrValue = attrs[key];
      if (Array.isArray(attrValue)) {
        return attrValue;
      }
      return undefined;
    }

    if (key === 'notes' || key === 'photoRef') {
      const topLevel = (person as any)[key];
      if (typeof topLevel === 'string') return topLevel;
      const attrValue = attrs[key];
      if (typeof attrValue === 'string') return attrValue;
      return undefined;
    }

    if (key === 'birthDate' || key === 'deathDate') {
      const propValue = props[key];
      if (typeof propValue === 'string') return propValue;
      if (propValue instanceof Date) return propValue.toISOString().slice(0, 10);
      const topLevel = (person as any)[key];
      if (typeof topLevel === 'string') return topLevel;
      const attrValue = attrs[key];
      if (typeof attrValue === 'string') return attrValue;
      if (attrValue instanceof Date) return attrValue.toISOString().slice(0, 10);
      return undefined;
    }

    if (key === 'gender') {
      const propGender = props.gender ?? props.genderIdentity;
      if (typeof propGender === 'string') return propGender;
      const attrGender = attrs.gender ?? attrs.genderIdentity;
      if (typeof attrGender === 'string') return attrGender;
      const topLevelGender = (person as any).gender ?? (person as any).genderIdentity;
      if (typeof topLevelGender === 'string') return topLevelGender;
      return undefined;
    }

    if (Object.prototype.hasOwnProperty.call(props, key)) {
      return props[key];
    }

    const topLevel = (person as any)[key];
    if (topLevel !== undefined) return topLevel;

    if (key === 'location') {
      const attrValue = attrs[key];
      if (typeof attrValue === 'string') return attrValue;
      return attrValue !== undefined ? String(attrValue ?? '') : undefined;
    }

    return attrs[key];
  }, [TAG_FIELDS, coerceBoolean]);

  const setAll = (updates: Record<string, any>) => {
    const cmds: ICommand[] = [];
    for (const p of persons) {
      cmds.push(new UpdatePersonCommand(p.id, normalizeUpdates(updates)));
    }
    onExecute(cmds);
  };

  const selectionKey = React.useMemo(() => {
    const sorted = [...ids].sort();
    return sorted.join('|');
  }, [ids]);

  const [fieldDrafts, setFieldDrafts] = React.useState<Record<string, string>>(() => {
    const cached = pendingFieldCache.get(selectionKey);
    return cached?.drafts ?? {};
  });
  const [lastSubmitted, setLastSubmitted] = React.useState<Record<string, string>>(() => {
    const cached = pendingFieldCache.get(selectionKey);
    return cached?.lastSubmitted ?? {};
  });

  const selectionKeyRef = React.useRef(selectionKey);
  const fieldDraftsRef = React.useRef(fieldDrafts);
  const lastSubmittedRef = React.useRef(lastSubmitted);
  const hasPersons = persons.length > 0;

  React.useEffect(() => {
    fieldDraftsRef.current = fieldDrafts;
  }, [fieldDrafts]);

  React.useEffect(() => {
    lastSubmittedRef.current = lastSubmitted;
  }, [lastSubmitted]);

  const persistCache = React.useCallback((key: string) => {
    if (!key) return;
    if (Object.keys(fieldDraftsRef.current).length === 0 && Object.keys(lastSubmittedRef.current).length === 0) {
      pendingFieldCache.delete(key);
      return;
    }
    pendingFieldCache.set(key, {
      drafts: { ...fieldDraftsRef.current },
      lastSubmitted: { ...lastSubmittedRef.current },
    });
  }, []);

  React.useEffect(() => {
    if (!hasPersons) return;
    const prevKey = selectionKeyRef.current;
    if (prevKey !== selectionKey) {
      persistCache(prevKey);
      const cached = pendingFieldCache.get(selectionKey);
      setFieldDrafts(cached?.drafts ?? {});
      setLastSubmitted(cached?.lastSubmitted ?? {});
      selectionKeyRef.current = selectionKey;
    }
  }, [hasPersons, selectionKey, persistCache]);

  React.useEffect(() => {
    if (!hasPersons) return;
    persistCache(selectionKeyRef.current);
  }, [fieldDrafts, hasPersons, lastSubmitted, persistCache]);

  React.useEffect(() => {
    if (!hasPersons) return;
    return () => {
      persistCache(selectionKeyRef.current);
    };
  }, [hasPersons, persistCache]);

  const setFieldDraft = React.useCallback((key: string, value: string | undefined) => {
    setFieldDrafts(prev => {
      if (value === undefined) {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key] === value) return prev;
      return { ...prev, [key]: value };
    });
  }, []);

  const recordSubmission = React.useCallback((key: string, value: string) => {
    setLastSubmitted(prev => {
      if (prev[key] === value) return prev;
      return { ...prev, [key]: value };
    });
  }, []);

  const resolveStringValue = (value: unknown): string => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') {
      return value === 'mixed' ? '' : value;
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    return '';
  };

  const computeResolvedString = React.useCallback((fieldKey: string): string => {
    const values = persons.map(p => resolveFieldValue(p, fieldKey));
    const mixed = computeMixed(values);
    return resolveStringValue(mixed);
  }, [persons, resolveFieldValue]);

  React.useEffect(() => {
    if (!hasPersons) return;
    if (Object.keys(lastSubmitted).length === 0) return;
    const ready: Record<string, string> = {};
    Object.entries(lastSubmitted).forEach(([key, submittedValue]) => {
      const resolved = computeResolvedString(key);
      if (resolved === submittedValue) {
        ready[key] = resolved;
      }
    });
    const keys = Object.keys(ready);
    if (keys.length === 0) return;
    setFieldDrafts(prev => {
      let changed = false;
      const next = { ...prev };
      keys.forEach(key => {
        if (next[key] !== undefined && next[key] === ready[key]) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setLastSubmitted(prev => {
      const next = { ...prev };
      keys.forEach(key => {
        delete next[key];
      });
      return next;
    });
  }, [hasPersons, lastSubmitted, computeResolvedString]);

  if (persons.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
        {t('panels.selectPersonsToEdit', 'Select person(s) to edit')}
      </div>
    );
  }

  const renderField = (def: any) => {
    if (!def) return null;
    const key = def.key;
    const baseTestId = `tid.genogram.field.${key}`;
    const values = persons.map(p => resolveFieldValue(p, key));
    const mixed = computeMixed(values);

    switch (def.kind) {
      case 'richtext':
        {
          const baseValue = resolveStringValue(mixed);
          const draftValue = fieldDrafts[key] ?? baseValue;
          return (
            <CommitOnCanvasClickTextarea
              value={draftValue}
              placeholder={mixed === 'mixed' ? 'Mixed' : (def.ui?.placeholder || '')}
              onChange={(e) => {
                const nextValue = e.target.value;
                setFieldDraft(key, nextValue);
              }}
              onBlur={() => {
                const currentDraft = fieldDrafts[key];
                if (currentDraft !== undefined && currentDraft !== baseValue) {
                  recordSubmission(key, currentDraft);
                  setAll({ [key]: currentDraft });
                }
              }}
              onCommit={() => {
                const currentDraft = fieldDrafts[key];
                if (currentDraft !== undefined && currentDraft !== baseValue) {
                  recordSubmission(key, currentDraft);
                  setAll({ [key]: currentDraft });
                }
              }}
              className="text-xs resize-none h-20"
              rows={3}
              dataTestId={`${baseTestId}.textarea`}
            />
          );
        }
      case 'string':
      case 'image':
        {
          const baseValue = resolveStringValue(mixed);
          const draftValue = fieldDrafts[key] ?? baseValue;
          const isMultiline = def.ui?.control === 'textarea';
          
          if (isMultiline) {
            return (
              <CommitOnCanvasClickTextarea
                value={draftValue}
                placeholder={mixed === 'mixed' ? 'Mixed' : (def.ui?.placeholder || '')}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setFieldDraft(key, nextValue);
                }}
                onBlur={() => {
                  const currentDraft = fieldDrafts[key];
                  if (currentDraft !== undefined && currentDraft !== baseValue) {
                    recordSubmission(key, currentDraft);
                    setAll({ [key]: currentDraft });
                  }
                }}
                onCommit={() => {
                  const currentDraft = fieldDrafts[key];
                  if (currentDraft !== undefined && currentDraft !== baseValue) {
                    recordSubmission(key, currentDraft);
                    setAll({ [key]: currentDraft });
                  }
                }}
                className="text-xs resize-none h-20"
                rows={3}
                dataTestId={`${baseTestId}.textarea`}
              />
            );
          }
          
          return (
            <CommitOnCanvasClickInput
              value={draftValue}
              placeholder={mixed === 'mixed' ? t('schema.mixed', 'Mixed') : (def.ui?.placeholder || '')}
              onChange={(e) => {
                const nextValue = e.target.value;
                setFieldDraft(key, nextValue);
              }}
              onBlur={() => {
                const currentDraft = fieldDrafts[key];
                if (currentDraft !== undefined && currentDraft !== baseValue) {
                  recordSubmission(key, currentDraft);
                  setAll({ [key]: currentDraft });
                }
              }}
              onCommit={() => {
                const currentDraft = fieldDrafts[key];
                if (currentDraft !== undefined && currentDraft !== baseValue) {
                  recordSubmission(key, currentDraft);
                  setAll({ [key]: currentDraft });
                }
              }}
              className="h-7 text-xs"
              dataTestId={`${baseTestId}.input`}
            />
          );
        }
      case 'number': {
        const baseValue = resolveStringValue(mixed);
        const draftValue = fieldDrafts[key] ?? baseValue;
        return (
          <Input
            type="number"
            inputMode="numeric"
            value={draftValue}
            placeholder={mixed === 'mixed' ? 'Mixed' : ''}
            onChange={(e) => {
              const nextValue = e.target.value;
              setFieldDraft(key, nextValue);
              recordSubmission(key, nextValue);
              setAll({ [key]: nextValue });
            }}
            className="h-7 text-xs"
            data-testid={`${baseTestId}.input`}
          />
        );
      }
      case 'date': {
        const baseValue = typeof mixed === 'string' ? formatDateForInput(mixed) : '';
        const draftValue = fieldDrafts[key] ?? baseValue;
        return (
          <Input
            type="date"
            value={draftValue}
            onChange={(e) => {
              const nextValue = e.target.value;
              setFieldDraft(key, nextValue);
              recordSubmission(key, nextValue);
              setAll({ [key]: nextValue });
            }}
            className="h-7 text-xs"
            data-testid={`${baseTestId}.input`}
          />
        );
      }
      case 'boolean': {
        const draftEntry = fieldDrafts[key];
        const baseValue = mixed === 'mixed' ? undefined : coerceBoolean(mixed);
        const draftValue = draftEntry !== undefined ? coerceBoolean(draftEntry) : undefined;
        const effectiveValue = draftValue ?? baseValue;
        const isMixed = mixed === 'mixed' && draftValue === undefined;
        const nextState = isMixed ? 'indeterminate' : effectiveValue === true ? 'checked' : 'unchecked';
        return (
          <div className="flex items-center gap-2">
            <Switch
              data-testid={`${baseTestId}.switch`}
              checked={effectiveValue === true}
              onCheckedChange={(nextChecked) => {
                const draftString = nextChecked ? 'true' : 'false';
                setFieldDraft(key, draftString);
                recordSubmission(key, draftString);
                setAll({ [key]: nextChecked });
              }}
              aria-checked={isMixed ? 'mixed' : effectiveValue === true}
              data-state={nextState}
              className="scale-90"
            />
            {isMixed && <span className="text-[10px] text-foreground/70">Mixed</span>}
          </div>
        );
      }
      case 'enum': {
        const current = typeof mixed === 'string' ? mixed : undefined;
        const draftValue = fieldDrafts[key];
        const effectiveValue = (draftValue ?? current) || undefined;
        const enumOptionLabel = (v: string) => {
          const hit = (def.options as { value: string; label: string }[] | undefined)?.find((o) => o.value === v);
          return hit?.label ?? v;
        };
        if (def.ui?.control === 'toggle-group' && (def.enumValues || []).length === 3) {
          return (
            <ToggleGroup
              data-testid={`${baseTestId}.toggle`}
              type="single"
              value={effectiveValue}
              onValueChange={(v) => {
                if (!v) {
                  setFieldDraft(key, undefined);
                  recordSubmission(key, '');
                  setAll({ [key]: '' });
                  return;
                }
                setFieldDraft(key, v as string);
                recordSubmission(key, v as string);
                setAll({ [key]: v as string });
              }}
              className="w-full justify-start h-7"
            >
              {(def.enumValues || []).map((v: string) => (
                <ToggleGroupItem key={v} value={v} className="flex-1 text-[11px] h-7 px-2">
                  {enumOptionLabel(v)}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          );
        }
        return (
          <Select
            value={effectiveValue}
            onValueChange={(v) => {
              setFieldDraft(key, v);
              recordSubmission(key, v);
              setAll({ [key]: v });
            }}
          >
            <SelectTrigger className="h-7 text-xs" data-testid={`${baseTestId}.select`}>
              <SelectValue
                placeholder={
                  mixed === 'mixed' ? t('schema.mixed', 'Mixed') : t('schema.selectPlaceholder', 'Select…')
                }
              />
            </SelectTrigger>
            <SelectContent>
              {(def.enumValues || []).map((v: string) => (
                <SelectItem key={v} value={v} className="text-xs">
                  {enumOptionLabel(v)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
      case 'color': {
        const current = typeof mixed === 'string' ? mixed : undefined;
        // Defaults: Fill → light gray, Stroke → pure black
        const fallback = def.key === 'fillColor' ? '#f5f5f5' : def.key === 'strokeColor' ? '#000000' : '#000000';
        const value = current ?? (mixed === 'mixed' ? '' : fallback);
        return (
          <Input
            value={value}
            placeholder={mixed === 'mixed' ? 'Mixed' : fallback}
            onChange={(e) => setAll({ [key]: e.target.value })}
            className="h-7 text-xs"
            data-testid={`${baseTestId}.input`}
          />
        );
      }
      case 'tags': {
        const tagValues = Array.isArray(mixed) ? mixed : [];
        const field: FieldManifest = {
          id: def.key,
          label: def.label || def.key,
          type: FieldType.MULTI_SELECT,
          tags: [],
          allowCustomOptions: Boolean((def as any).allowCustomOptions),
          options: Array.isArray((def as any).options) ? (def as any).options : [],
          namespace: (def as any).namespace,
        } as any;
        return (
          <div data-testid={`${baseTestId}.multi`}>
            <MultiSelectRenderer
              field={field}
              value={tagValues}
              onChange={(v) => setAll({ [key]: v })}
            />
          </div>
        );
      }
      default:
        {
          const baseValue = resolveStringValue(mixed);
          const draftValue = fieldDrafts[key] ?? baseValue;
          return (
            <Input
              value={draftValue}
              placeholder={mixed === 'mixed' ? 'Mixed' : ''}
              onChange={(e) => {
                const nextValue = e.target.value;
                setFieldDraft(key, nextValue);
                recordSubmission(key, nextValue);
                setAll({ [key]: nextValue });
              }}
              className="h-7 text-xs"
              data-testid={`${baseTestId}.input`}
            />
          );
        }
    }
  };

  const renderSection = (sectionKey: keyof typeof SECTION_CONFIG, isFirst: boolean) => {
    const section = SECTION_CONFIG[sectionKey];
    const isShowingAll = showAllFields[sectionKey] || false;
    let defs = isShowingAll ? allDefsByGroup[sectionKey] : filteredDefsByGroup[sectionKey];

    // Special logic for deceased field - hide deathDate unless deceased is true
    const deceasedMixed = computeMixed(persons.map(p => resolveFieldValue(p, 'deceased')));
    const showDeathDate = deceasedMixed === true;
    if (!showDeathDate) {
      defs = defs.filter(d => d.key !== 'deathDate');
    }

    const totalCount = allDefsByGroup[sectionKey].length;
    const visibleCount = defs.length;
    const hiddenCount = totalCount - visibleCount;
    const hasHiddenFieldsOnly = filteredDefsByGroup[sectionKey].length === 0 && totalCount > 0;

    if (totalCount === 0) return null;

    const isOpen = sectionsOpen[sectionKey];

    return (
      <React.Fragment key={sectionKey}>
        {!isFirst && <div className="h-px bg-border my-4" />}

        <div className="px-3 pr-6">
          <button
            type="button"
            onClick={() => toggleSection(sectionKey, hasHiddenFieldsOnly)}
            className="flex items-center justify-between w-full mb-3 group"
            data-testid={`tid.genogram.section.${sectionKey}`}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-[11px] font-semibold text-foreground uppercase tracking-wider">
                {t(section.titleKey)}
              </h3>
              {hiddenCount > 0 && !isShowingAll && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleShowAllFields(sectionKey);
                  }}
                  className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('schema.additionalCount', { count: hiddenCount })}
                </button>
              )}
              {isShowingAll && hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleShowAllFields(sectionKey);
                  }}
                  className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('schema.hideCount', { count: hiddenCount })}
                </button>
              )}
            </div>
            <div className="opacity-70 group-hover:opacity-100 transition-opacity">
              {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            </div>
          </button>

          {isOpen && (
            <div className="space-y-3">
              {defs.map(def => (
                <div key={def.key} data-testid={`tid.genogram.field.${def.key}`}>
                  <Label className="text-[11px] font-normal text-foreground mb-1.5 block">
                    {def.label || def.key}
                  </Label>
                  {renderField(def)}
                </div>
              ))}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  };

  return (
    <>
      {renderSection('identity', true)}
      {renderSection('medical', false)}
      {renderSection('legal', false)}
      {renderSection('financial', false)}
      {renderSection('demographics', false)}
      {renderSection('relationships', false)}
      {renderSection('research', false)}
      {renderSection('context', false)}
      {renderSection('extras', false)}
    </>
  );
};

export class SchemaDrivenPersonTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-person-schema-edit',
    name: i18n.t('genogram:tools.personSchema', { defaultValue: 'Person (Schema)' }) as string,
    icon: 'user',
    category: 'edit',
    description: 'Edit person using manifest-driven schema',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram'],
    requiresSelection: true,
    minSelection: 1,
    ui: { toolbar: false, contextMenu: false, propertyPanel: true, sidebar: false, floatingPanel: false, statusBar: false },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.personSchema', 'Person (Schema)');
  }

  canExecute(context: IToolContext): boolean {
    const ids = Array.from(context.selection);
    if (ids.length === 0) return false;
    const nodes = useDiagramStore.getState().nodes.filter(n => ids.includes(n.id));
    if (nodes.length === 0) return false;
    // Only show for person nodes; hide for text-only (or mixed non-person) selections
    return nodes.every(n => n.type === 'person');
  }

  execute(params?: any): ICommand | ICommand[] | null {
    if (!params) return null;
    if (Array.isArray(params)) return params; // commands from UI
    const { personIds, updates } = params || {};
    if (!Array.isArray(personIds) || !updates) return null;
    return personIds.map((id: string) => new UpdatePersonCommand(id, updates));
  }

  getPropertyPanelComponent() {
    return SchemaDrivenPersonPanel;
  }
}
